'use client';

import * as React from 'react';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { EditorContent, getMarkRange, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { IconSend01 } from 'icon/Communication/send-01';
import { IconCode02 } from 'icon/Development/code-02';
import { IconBlockquote } from 'icon/Editor/blockquote';
import { IconBold01 } from 'icon/Editor/bold-01';
import { IconDotpoints01 } from 'icon/Editor/dotpoints-01';
import { IconItalic01 } from 'icon/Editor/italic-01';
import { IconNumberedList } from 'icon/Editor/numbered-list';
import { IconStrikethrough01 } from 'icon/Editor/strikethrough-01';
import { IconUnderline01 } from 'icon/Editor/underline-01';
import { IconDotsHorizontal } from 'icon/General/dots-horizontal';
import { IconLink03 } from 'icon/General/link-03';
import { IconXClose } from 'icon/General/x-close';
import {
  EMPTY_COMMENT,
  safeCommentLink,
  type CommentDocument,
} from 'lib/comments';
import { useTranslations } from 'next-intl';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'ui';
import { COMMENT_TEXT_STYLES } from './comment-content';

export const COMMENT_BUTTON =
  'inline-flex items-center justify-center gap-1 rounded-md border border-secondary px-3 py-2 text-sm font-medium text-primary hover:bg-secondary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:cursor-not-allowed disabled:opacity-50';
export const COMMENT_INPUT =
  'w-full rounded-md border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder:text-placeholder focus-visible:outline-2 focus-visible:outline-brand-500';

export function CommentEditor({
  initialDocument,
  pending,
  onSubmit,
  onCancel,
  signedIn,
  label,
}: {
  initialDocument?: CommentDocument;
  pending: boolean;
  onSubmit: (document: CommentDocument) => Promise<void>;
  onCancel?: () => void;
  signedIn: boolean;
  label: string;
}) {
  const t = useTranslations('Blog.Comments');
  const [hasText, setHasText] = React.useState(!!initialDocument);
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [href, setHref] = React.useState('');
  const [existingLink, setExistingLink] = React.useState(false);
  const [selectedText, setSelectedText] = React.useState('');
  const selection = React.useRef<{ from: number; to: number } | undefined>(
    undefined,
  );
  const linkInputId = React.useId();
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        validate: (url) => !!safeCommentLink(url),
        HTMLAttributes: {
          rel: 'nofollow ugc noopener noreferrer',
          target: '_blank',
        },
      }),
    ],
    content: initialDocument ?? EMPTY_COMMENT,
    editorProps: {
      attributes: {
        class: `min-h-24 p-3 outline-none ${COMMENT_TEXT_STYLES} [&>p:first-child]:mt-0 [&>p:last-child]:mb-0`,
        role: 'textbox',
        'aria-label': t('WriteComment'),
        'aria-multiline': 'true',
      },
    },
    onUpdate: ({ editor }) => setHasText(!!editor.getText().trim()),
  });
  React.useEffect(() => {
    editor?.setEditable(!pending);
  }, [editor, pending]);
  const actions = editor
    ? [
        {
          title: t('Bold'),
          Icon: IconBold01,
          active: editor.isActive('bold'),
          run: () => editor.chain().focus().toggleBold().run(),
        },
        {
          title: t('Italic'),
          Icon: IconItalic01,
          active: editor.isActive('italic'),
          run: () => editor.chain().focus().toggleItalic().run(),
        },
        {
          title: t('Underline'),
          Icon: IconUnderline01,
          active: editor.isActive('underline'),
          run: () => editor.chain().focus().toggleUnderline().run(),
        },
        {
          title: t('Strike'),
          secondary: true,
          Icon: IconStrikethrough01,
          active: editor.isActive('strike'),
          run: () => editor.chain().focus().toggleStrike().run(),
        },
        {
          title: t('Code'),
          Icon: IconCode02,
          active: editor.isActive('code'),
          run: () => editor.chain().focus().toggleCode().run(),
        },
        {
          title: t('Quote'),
          secondary: true,
          Icon: IconBlockquote,
          active: editor.isActive('blockquote'),
          run: () => editor.chain().focus().toggleBlockquote().run(),
        },
        {
          title: t('BulletList'),
          secondary: true,
          Icon: IconDotpoints01,
          active: editor.isActive('bulletList'),
          run: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
          title: t('OrderedList'),
          secondary: true,
          Icon: IconNumberedList,
          active: editor.isActive('orderedList'),
          run: () => editor.chain().focus().toggleOrderedList().run(),
        },
        {
          title: t('Link'),
          Icon: IconLink03,
          active: editor.isActive('link'),
          disabled: editor.state.selection.empty && !editor.isActive('link'),
          run: () => {
            const linked = editor.isActive('link');
            const linkMark = editor.schema.marks.link;
            const range =
              linked && linkMark && editor.state.selection.empty
                ? getMarkRange(
                    editor.state.selection.$from,
                    linkMark,
                    editor.getAttributes('link'),
                  )
                : undefined;
            const { from, to } = range ?? editor.state.selection;
            selection.current = { from, to };
            setSelectedText(editor.state.doc.textBetween(from, to, '\n'));
            setExistingLink(linked);
            setHref(linked ? editor.getAttributes('link').href || '' : '');
            setLinkOpen(true);
          },
        },
      ]
    : [];
  return (
    <>
      <form
        className="@container"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!editor || !hasText || pending || !signedIn) return;
          try {
            await onSubmit(editor.getJSON() as CommentDocument);
            editor.commands.clearContent();
            setHasText(false);
          } catch {
            /* The caller displays the error and keeps the draft. */
          }
        }}
      >
        <div className="overflow-hidden rounded-lg border border-secondary bg-primary focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-inset focus-within:ring-brand-500/30">
          {editor ? (
            <div className="relative">
              <EditorContent editor={editor} />
              {!hasText && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute start-3 top-3 text-sm leading-relaxed text-placeholder"
                >
                  {t('WriteComment')}
                </span>
              )}
            </div>
          ) : (
            <div
              className="h-28 animate-pulse bg-secondary"
              aria-label={t('Loading')}
            />
          )}
          <div className="flex items-center gap-2 border-t border-secondary px-2 py-1.5">
            <div
              className="flex min-w-0 flex-1 items-center gap-0.5"
              role="toolbar"
              aria-label={t('Formatting')}
            >
              <div className="flex min-w-0 items-center gap-0.5 overflow-x-auto p-0.5 [scrollbar-width:none]">
                {actions.map((action) => (
                  <button
                    key={action.title}
                    type="button"
                    aria-label={action.title}
                    title={action.title}
                    aria-pressed={action.active}
                    disabled={pending || action.disabled}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={action.run}
                    className={`${action.secondary ? 'hidden @[32rem]:inline-flex' : 'inline-flex'} size-8 shrink-0 items-center justify-center rounded hover:bg-secondary-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-500 disabled:opacity-40 ${action.active ? 'bg-secondary text-primary' : 'text-tertiary'}`}
                  >
                    <action.Icon aria-hidden="true" className="size-4" />
                  </button>
                ))}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('MoreFormatting')}
                    title={t('MoreFormatting')}
                    disabled={pending || !editor}
                    onMouseDown={(event) => event.preventDefault()}
                    className="inline-flex size-8 shrink-0 items-center justify-center rounded text-tertiary hover:bg-secondary-hover focus-visible:outline-2 focus-visible:outline-brand-500 @[32rem]:hidden"
                  >
                    <IconDotsHorizontal aria-hidden="true" className="size-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="border-secondary bg-primary text-primary"
                  onCloseAutoFocus={(event) => event.preventDefault()}
                >
                  {actions
                    .filter((action) => action.secondary)
                    .map((action) => (
                      <DropdownMenuItem
                        key={action.title}
                        disabled={pending}
                        onSelect={action.run}
                        className={action.active ? 'bg-secondary' : ''}
                      >
                        <action.Icon aria-hidden="true" className="size-4" />
                        {action.title}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="ms-auto flex shrink-0 items-center gap-1">
              {onCancel && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 text-tertiary"
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                  onClick={onCancel}
                  disabled={pending}
                >
                  <IconXClose aria-hidden="true" className="size-4" />
                </Button>
              )}
              <Button
                className="h-8 gap-1.5 px-2.5 text-sm"
                type="submit"
                disabled={!editor || !hasText || pending || !signedIn}
              >
                <IconSend01 aria-hidden="true" className="size-4" />
                {pending ? t('Sending') : label}
              </Button>
            </div>
          </div>
        </div>
      </form>
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent
          closeLabel={t('Close')}
          className="w-[calc(100vw-2rem)] max-w-md rounded-xl border-secondary bg-primary text-primary sm:w-[calc(100vw-2rem)] sm:max-w-md"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (editor && !editor.isDestroyed && selection.current)
              editor.chain().focus().setTextSelection(selection.current).run();
          }}
        >
          <DialogHeader className="pe-6">
            <DialogTitle>{t('Link')}</DialogTitle>
            <DialogDescription className="max-h-24 overflow-y-auto whitespace-pre-wrap break-words border-s-2 border-secondary ps-3 text-start text-sm text-secondary">
              {selectedText}
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              const url = safeCommentLink(href);
              if (!editor || !selection.current || !url || pending) return;
              editor
                .chain()
                .setTextSelection(selection.current)
                .extendMarkRange('link')
                .setLink({ href: url })
                .run();
              setLinkOpen(false);
            }}
          >
            <div className="space-y-1.5">
              <label htmlFor={linkInputId} className="text-sm text-secondary">
                {t('LinkUrl')}
              </label>
              <input
                id={linkInputId}
                className={COMMENT_INPUT}
                type="url"
                placeholder="https://"
                value={href}
                onChange={(event) => setHref(event.target.value)}
                required
                maxLength={2048}
                disabled={pending}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              {existingLink && (
                <Button
                  variant="ghost"
                  className="me-auto text-tertiary"
                  disabled={pending}
                  onClick={() => {
                    if (!editor || !selection.current) return;
                    editor
                      .chain()
                      .setTextSelection(selection.current)
                      .extendMarkRange('link')
                      .unsetLink()
                      .run();
                    setLinkOpen(false);
                  }}
                >
                  {t('RemoveLink')}
                </Button>
              )}
              <Button
                variant="ghost"
                disabled={pending}
                onClick={() => setLinkOpen(false)}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!safeCommentLink(href) || pending}
              >
                {t('Save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
